/**
 * Status icons, one spec for web and mobile ("Marker spec v3": Figma "History logs" 32249:38112, the design system's
 * status set). 20 x 20, centre anchor.
 * - done (Figma "Pass"): Green #36AB19 circle, white 14 px check centred;
 * - rejected (Figma red X; reserved for a future Fail result, not used yet): Red #E02020 circle, white ~10 px X;
 * - pending (v3.1, "use red color for pending"): solid Red #E02020 circle, 2 px white outline, no glyph;
 * - partial (v3.1): 2 px white outline, left half Green #36AB19, right half Red #E02020, no glyph.
 * statusIconSvg draws the 20 px list icons (shown at 16 px); statusMarkerSvg draws the map markers at the layer's
 * point size. The X glyph tells rejected from pending.
 */
export type StatusIcon = 'done' | 'partial' | 'pending' | 'rejected';

export const STATUS_ICON_SIZE = 20;
export const STATUS_GREEN = '#36AB19';
export const STATUS_RED = '#E02020';

/** Figma "Pass" check, 14 x 14 box. */
export const STATUS_CHECK_PATH = 'M2.91667 8.01596L6.09073 11.0833L11.6667 2.91667';
const X_PATHS = ['M1.00004 8.84504L8.84552 1', 'M8.84547 8.84504L1 1'];

const open = `<svg xmlns="http://www.w3.org/2000/svg" width="${STATUS_ICON_SIZE}" height="${STATUS_ICON_SIZE}" viewBox="0 0 20 20">`;
const stroke = 'stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"';

/** The list icon as SVG markup (20 px box). */
export function statusIconSvg(status: StatusIcon): string {
  switch (status) {
    case 'done':
      // The 14 px check centred: offset (20 - 14) / 2.
      return `${open}<circle cx="10" cy="10" r="10" fill="${STATUS_GREEN}"/>`
        + `<path transform="translate(3 3)" d="${STATUS_CHECK_PATH}" ${stroke}/></svg>`;
    case 'rejected':
      // The 9.85 px X drawn ~10 px, centred.
      return `${open}<circle cx="10" cy="10" r="10" fill="${STATUS_RED}"/>`
        + `<g transform="translate(5.077 5.077)">${X_PATHS.map(d => `<path d="${d}" ${stroke}/>`).join('')}</g></svg>`;
    case 'partial':
      // Outline r 9 / width 2: outer edge at the 20 px box, fill inside r 8.
      return `${open}<circle cx="10" cy="10" r="9" fill="${STATUS_RED}"/>`
        + `<path d="M10 1a9 9 0 0 0 0 18z" fill="${STATUS_GREEN}"/>`
        + `<circle cx="10" cy="10" r="9" fill="none" stroke="#FFFFFF" stroke-width="2"/></svg>`;
    case 'pending':
    default:
      return `${open}<circle cx="10" cy="10" r="9" fill="${STATUS_RED}" stroke="#FFFFFF" stroke-width="2"/></svg>`;
  }
}

/** The layer's point symbol, as the published style stores it (wizard "Point size", default 8). */
export interface PointSymbolLike { geometry?: string; size?: number; strokeColor?: string; strokeWidth?: number }

/**
 * Map marker size ("Marker size v2"): the layer's own point size, at least 16 px (the chooser icon size), with the
 * layer's outline colour and its width, at least 2 px (white 2 when the style has none). Only the fill shows the status.
 */
export function statusMarkerMetrics(symbols: PointSymbolLike[] | null | undefined): { diameter: number; strokeColor: string; strokeWidth: number } {
  const point = (symbols || []).find(symbol => symbol?.geometry === 'point');
  const size = Number(point?.size);
  const width = Number(point?.strokeWidth);
  return {
    diameter: Math.max(Number.isFinite(size) ? size : 0, MIN_MARKER_DIAMETER),
    strokeColor: typeof point?.strokeColor === 'string' && point.strokeColor ? point.strokeColor : '#FFFFFF',
    strokeWidth: Math.max(Number.isFinite(width) ? width : 0, MIN_MARKER_STROKE),
  };
}

export const MIN_MARKER_DIAMETER = 16;
export const MIN_MARKER_STROKE = 2;
/** Map markers are drawn at this multiple of their displayed size (crisp up to 4x screens); see statusMarkerSvg. */
export const MARKER_RASTER_SCALE = 4;

/**
 * The map marker of a status at `diameter` px with the given outline: the same fills as the list icons; the done check
 * (Figma 14 px path) scaled to 60% of the diameter and the rejected X to 50%, centred.
 */
export function statusMarkerSvg(status: StatusIcon, metrics: { diameter: number; strokeColor: string; strokeWidth: number }): string {
  const d = metrics.diameter;
  const c = d / 2;
  const sw = metrics.strokeWidth;
  const r = Math.max(0.5, c - sw / 2);
  // Intrinsic size MARKER_RASTER_SCALE x the displayed diameter (same viewBox): the picture marker is rasterised at its
  // intrinsic size, so a 1x image upscaled on hi-DPI screens smudged the check and greyed the white outline.
  const px = d * MARKER_RASTER_SCALE;
  const svg = (body: string) => `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 ${d} ${d}">${body}</svg>`;
  const outline = sw > 0 ? `<circle cx="${c}" cy="${c}" r="${r}" fill="none" stroke="${metrics.strokeColor}" stroke-width="${sw}"/>` : '';
  const glyph = (paths: string[], box: number, share: number) => {
    const scale = (d * share) / box;
    const offset = (d - d * share) / 2;
    return `<g transform="translate(${offset} ${offset}) scale(${scale})">`
      + paths.map(path => `<path d="${path}" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`).join('')
      + '</g>';
  };
  switch (status) {
    case 'done':
      return svg(`<circle cx="${c}" cy="${c}" r="${r}" fill="${STATUS_GREEN}"/>${outline}${glyph([STATUS_CHECK_PATH], 14, 0.6)}`);
    case 'rejected':
      return svg(`<circle cx="${c}" cy="${c}" r="${r}" fill="${STATUS_RED}"/>${outline}${glyph(X_PATHS, 9.84552, 0.5)}`);
    case 'partial':
      return svg(`<circle cx="${c}" cy="${c}" r="${r}" fill="${STATUS_RED}"/>`
        + `<path d="M${c} ${c - r}a${r} ${r} 0 0 0 0 ${2 * r}z" fill="${STATUS_GREEN}"/>${outline}`);
    case 'pending':
    default:
      return svg(`<circle cx="${c}" cy="${c}" r="${r}" fill="${STATUS_RED}"/>${outline}`);
  }
}

/** A data URL for the map marker of a status (see statusMarkerSvg). */
export function statusMarkerDataUrl(status: StatusIcon, metrics: { diameter: number; strokeColor: string; strokeWidth: number }): string {
  return `data:image/svg+xml;utf8,${encodeURIComponent(statusMarkerSvg(status, metrics))}`;
}
